import { useEffect, useState } from 'preact/hooks';
import {
  isKanbanOpenSignal,
  kanbanTasksSignal,
  filteredKanbanTasks,
  isLoadingKanbanSignal,
  loadAllKanbanTasks,
  updateKanbanTaskStatus,
  selectedKanbanTaskSignal,
  kanbanFilterStakeholderSignal,
  kanbanFilterPrioritySignal,
  kanbanFilterDueDateSignal,
  kanbanFilterAssigneeSignal,
  kanbanSearchQuerySignal
} from '../../state/kanban.store';
import { TaskDetailModal } from './TaskDetailModal';
import {
  X, Search, Filter, RefreshCw, User, Calendar, CheckSquare, Clock,
  CheckCircle2, Ban, Sparkles, Link as LinkIcon, FileText, ChevronDown, ChevronUp, RotateCcw
} from 'lucide-preact';

export function KanbanBoardModal() {
  const isOpen = isKanbanOpenSignal.value;
  if (!isOpen) return null;

  const tasks = filteredKanbanTasks.value;
  const allTasks = kanbanTasksSignal.value;
  const isLoading = isLoadingKanbanSignal.value;

  const [draggedTaskId, setDraggedTaskId] = useState<number | null>(null);
  const [mobileActiveColumn, setMobileActiveColumn] = useState<'pending' | 'in_progress' | 'completed' | 'cancelled'>('pending');
  const [isMobileFilterExpanded, setIsMobileFilterExpanded] = useState<boolean>(false);

  useEffect(() => {
    loadAllKanbanTasks();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        isKanbanOpenSignal.value = false;
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        const searchInput = document.getElementById('kanban-search-input');
        if (searchInput) (searchInput as HTMLInputElement).focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Extrair lista única de Stakeholders a partir das tarefas para o Select
  const uniqueStakeholders = Array.from(
    new Map(
      allTasks.map(t => [t.contact_phone, { phone: t.contact_phone, name: t.stakeholder_name || t.contact_phone }])
    ).values()
  );

  const columns: { id: 'pending' | 'in_progress' | 'completed' | 'cancelled'; title: string; shortTitle: string; icon: any; color: string; badgeBg: string; activeTabBg: string }[] = [
    { id: 'pending', title: 'Pendentes', shortTitle: 'Pendentes', icon: Clock, color: 'border-amber-500/40', badgeBg: 'bg-amber-500/20 text-amber-200 border-amber-500/40', activeTabBg: 'bg-amber-500/20 text-amber-200 border-amber-500/50' },
    { id: 'in_progress', title: 'Em Andamento', shortTitle: 'Andamento', icon: Sparkles, color: 'border-cyan-500/40', badgeBg: 'bg-cyan-500/20 text-cyan-200 border-cyan-500/40', activeTabBg: 'bg-cyan-500/20 text-cyan-200 border-cyan-500/50' },
    { id: 'completed', title: 'Concluídas', shortTitle: 'Concluídas', icon: CheckCircle2, color: 'border-emerald-500/40', badgeBg: 'bg-emerald-500/20 text-emerald-200 border-emerald-500/40', activeTabBg: 'bg-emerald-500/20 text-emerald-200 border-emerald-500/50' },
    { id: 'cancelled', title: 'Canceladas', shortTitle: 'Canceladas', icon: Ban, color: 'border-slate-700', badgeBg: 'bg-slate-800 text-slate-300 border-slate-700', activeTabBg: 'bg-slate-800 text-slate-300 border-slate-600' }
  ];

  // Active filters count for mobile toggle
  const activeFiltersCount = (
    (kanbanSearchQuerySignal.value ? 1 : 0) +
    (kanbanFilterStakeholderSignal.value !== 'all' ? 1 : 0) +
    (kanbanFilterPrioritySignal.value !== 'all' ? 1 : 0) +
    (kanbanFilterDueDateSignal.value !== 'all' ? 1 : 0) +
    (kanbanFilterAssigneeSignal.value !== 'all' ? 1 : 0)
  );

  const resetAllFilters = () => {
    kanbanSearchQuerySignal.value = '';
    kanbanFilterStakeholderSignal.value = 'all';
    kanbanFilterPrioritySignal.value = 'all';
    kanbanFilterDueDateSignal.value = 'all';
    kanbanFilterAssigneeSignal.value = 'all';
  };

  const handleDragStart = (e: DragEvent, taskId: number) => {
    setDraggedTaskId(taskId);
    if (e.dataTransfer) {
      e.dataTransfer.setData('text/plain', String(taskId));
      e.dataTransfer.effectAllowed = 'move';
    }
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'move';
    }
  };

  const handleDrop = async (e: DragEvent, targetStatus: 'pending' | 'in_progress' | 'completed' | 'cancelled') => {
    e.preventDefault();
    const idStr = e.dataTransfer?.getData('text/plain') || String(draggedTaskId);
    const taskId = Number(idStr);

    if (taskId) {
      await updateKanbanTaskStatus(taskId, targetStatus);
    }
    setDraggedTaskId(null);
  };

  const handleStatusQuickChange = async (e: Event, taskId: number, newStatus: 'pending' | 'in_progress' | 'completed' | 'cancelled') => {
    e.stopPropagation();
    await updateKanbanTaskStatus(taskId, newStatus);
  };

  return (
    <div class="fixed inset-0 z-[90] flex items-center justify-center p-0 sm:p-4 bg-slate-950/85 backdrop-blur-md animate-fade-in">
      <div class="w-full h-full sm:max-w-7xl sm:max-h-[95vh] bg-slate-950 sm:border sm:border-slate-800 sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden">
        
        {/* Modal Topbar */}
        <div class="p-3.5 sm:p-5 border-b border-slate-800 flex items-center justify-between gap-3 bg-slate-900/90 shrink-0">
          <div class="flex items-center gap-2.5 min-w-0">
            <div class="w-9 h-9 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400 shrink-0">
              <CheckSquare class="w-5 h-5" />
            </div>
            <div class="min-w-0">
              <h2 class="text-sm sm:text-base font-extrabold text-slate-100 flex items-center gap-2 truncate">
                <span class="truncate">Kanban de Tarefas</span>
                <span class="px-2 py-0.5 rounded-full text-xs font-mono bg-cyan-500/20 text-cyan-300 border border-cyan-400/30 shrink-0">
                  {tasks.length}
                </span>
              </h2>
              <p class="text-[11px] sm:text-xs text-slate-400 truncate hidden xs:block">
                Gestão global de pendências agrupadas por Stakeholders.
              </p>
            </div>
          </div>

          <div class="flex items-center gap-1.5 sm:gap-2 shrink-0">
            {activeFiltersCount > 0 && (
              <button
                onClick={resetAllFilters}
                class="hidden md:flex px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-cyan-300 text-xs font-bold rounded-xl transition items-center gap-1.5 cursor-pointer border border-slate-700"
                title="Limpar todos os filtros ativos"
              >
                <RotateCcw class="w-3.5 h-3.5 text-cyan-400" />
                <span>Limpar Filtros ({activeFiltersCount})</span>
              </button>
            )}
            <button
              onClick={() => loadAllKanbanTasks()}
              disabled={isLoading}
              class="min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold rounded-xl transition flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
              title="Recarregar tarefas"
            >
              <RefreshCw class={`w-4 h-4 sm:w-3.5 sm:h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
              <span class="hidden sm:inline">Atualizar</span>
            </button>
            <button
              onClick={() => isKanbanOpenSignal.value = false}
              class="min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 rounded-xl transition cursor-pointer"
              title="Fechar (Esc)"
            >
              <X class="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Filter Controls Toolbar */}
        <div class="p-3 sm:p-4 bg-slate-900/60 border-b border-slate-800 flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 shrink-0 text-xs">
          
          <div class="flex items-center gap-2 w-full sm:w-auto flex-1">
            {/* Search Box */}
            <div class="relative flex-1">
              <Search class="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                id="kanban-search-input"
                type="text"
                value={kanbanSearchQuerySignal.value}
                onInput={(e) => kanbanSearchQuerySignal.value = (e.target as HTMLInputElement).value}
                placeholder="Buscar por descrição, nota ou responsável (Cmd+K)..."
                class="w-full pl-9 pr-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 focus:outline-none focus:border-cyan-500 min-h-[40px]"
              />
            </div>

            {/* Mobile Filter Expand Toggle Button */}
            <button
              onClick={() => setIsMobileFilterExpanded(!isMobileFilterExpanded)}
              class={`sm:hidden min-h-[40px] px-3 py-2 rounded-xl border flex items-center gap-1.5 text-xs font-bold transition cursor-pointer shrink-0 ${
                activeFiltersCount > 0
                  ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40'
                  : 'bg-slate-950 text-slate-300 border-slate-800'
              }`}
            >
              <Filter class="w-4 h-4 text-cyan-400" />
              <span>Filtros</span>
              {activeFiltersCount > 0 && (
                <span class="px-1.5 py-0.2 rounded-full bg-cyan-400 text-slate-950 font-extrabold text-[10px]">
                  {activeFiltersCount}
                </span>
              )}
              {isMobileFilterExpanded ? <ChevronUp class="w-3.5 h-3.5" /> : <ChevronDown class="w-3.5 h-3.5" />}
            </button>
          </div>

          {/* Filter Selects Grid (Always visible on SM+, Collapsible on Mobile) */}
          <div class={`${isMobileFilterExpanded ? 'flex' : 'hidden'} sm:flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-800/80`}>
            
            {/* Stakeholder Select */}
            <select
              value={kanbanFilterStakeholderSignal.value}
              onChange={(e) => kanbanFilterStakeholderSignal.value = (e.target as HTMLSelectElement).value}
              class="px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 font-medium focus:outline-none focus:border-cyan-500 cursor-pointer min-h-[40px]"
            >
              <option value="all">Todos os Stakeholders</option>
              {uniqueStakeholders.map(s => (
                <option key={s.phone} value={s.phone}>
                  {s.name} ({s.phone})
                </option>
              ))}
            </select>

            {/* Priority Select */}
            <select
              value={kanbanFilterPrioritySignal.value}
              onChange={(e) => kanbanFilterPrioritySignal.value = (e.target as HTMLSelectElement).value}
              class="px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 font-medium focus:outline-none focus:border-cyan-500 cursor-pointer min-h-[40px]"
            >
              <option value="all">Todas as Prioridades</option>
              <option value="high">Alta Prioridade</option>
              <option value="medium">Média Prioridade</option>
              <option value="low">Baixa Prioridade</option>
            </select>

            {/* Due Date Select */}
            <select
              value={kanbanFilterDueDateSignal.value}
              onChange={(e) => kanbanFilterDueDateSignal.value = (e.target as HTMLSelectElement).value}
              class="px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-200 font-medium focus:outline-none focus:border-cyan-500 cursor-pointer min-h-[40px]"
            >
              <option value="all">Todos os Prazos</option>
              <option value="overdue">Atrasadas</option>
              <option value="today">Para Hoje</option>
              <option value="upcoming">Futuras</option>
              <option value="no_due_date">Sem Prazo</option>
            </select>

            {/* Assignee Filter Toggle */}
            <div class="flex items-center justify-center bg-slate-950 p-1 rounded-xl border border-slate-800 min-h-[40px]">
              <button
                onClick={() => kanbanFilterAssigneeSignal.value = 'all'}
                class={`flex-1 sm:flex-none px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer min-h-[32px] flex items-center justify-center ${
                  kanbanFilterAssigneeSignal.value === 'all' ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-400/40' : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                Todas
              </button>
              <button
                onClick={() => kanbanFilterAssigneeSignal.value = 'mine'}
                class={`flex-1 sm:flex-none px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer min-h-[32px] flex items-center justify-center ${
                  kanbanFilterAssigneeSignal.value === 'mine' ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-400/40' : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                Minhas
              </button>
            </div>

          </div>

        </div>

        {/* Global Empty Filter State */}
        {tasks.length === 0 ? (
          <div class="flex-1 flex flex-col items-center justify-center p-8 text-center bg-slate-950/60 rounded-2xl m-4 border border-dashed border-slate-800/80 animate-fade-in">
            <div class="w-14 h-14 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400 mb-4 shadow-inner">
              <Filter class="w-7 h-7 text-cyan-400" />
            </div>
            <h3 class="text-base font-extrabold text-slate-100 mb-1">
              Nenhuma tarefa encontrada com os filtros atuais
            </h3>
            <p class="text-xs text-slate-400 max-w-md mb-5 leading-relaxed">
              Não há pendências correspondentes aos critérios de busca ou filtros selecionados. Tente redefinir a busca ou limpar a filtragem.
            </p>
            {activeFiltersCount > 0 && (
              <button
                onClick={resetAllFilters}
                class="px-4 py-2.5 bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-300 border border-cyan-500/40 rounded-xl text-xs font-bold transition flex items-center gap-2 cursor-pointer shadow-sm min-h-[44px]"
              >
                <RotateCcw class="w-4 h-4 text-cyan-400" />
                <span>Limpar Todos os Filtros</span>
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Mobile Column Navigation Tabs (Only visible on Mobile < sm) */}
            <div class="sm:hidden bg-slate-900 border-b border-slate-800 p-2 flex items-center gap-1.5 overflow-x-auto shrink-0">
              {columns.map(col => {
                const count = tasks.filter(t => (t.status || 'pending') === col.id).length;
                const ColIcon = col.icon;
                const isActive = mobileActiveColumn === col.id;

                return (
                  <button
                    key={col.id}
                    onClick={() => setMobileActiveColumn(col.id)}
                    class={`flex-1 min-w-[100px] px-2.5 py-2 rounded-xl border text-xs font-bold transition flex items-center justify-center gap-1.5 cursor-pointer shrink-0 min-h-[44px] ${
                      isActive ? col.activeTabBg : 'bg-slate-950 text-slate-400 border-slate-800 hover:text-slate-200'
                    }`}
                  >
                    <ColIcon class="w-3.5 h-3.5 shrink-0" />
                    <span class="truncate">{col.shortTitle}</span>
                    <span class="px-1.5 py-0.2 rounded-full text-[10px] font-mono bg-slate-950/80 shrink-0">
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Mobile View: Single Active Column Task List */}
            <div class="sm:hidden flex-1 p-3 overflow-y-auto space-y-3">
              {(() => {
                const col = columns.find(c => c.id === mobileActiveColumn)!;
                const colTasks = tasks.filter(t => (t.status || 'pending') === col.id);
                const ColIcon = col.icon;

                if (colTasks.length === 0) {
                  return (
                    <div class="h-48 border border-dashed border-slate-800/80 rounded-2xl flex flex-col items-center justify-center p-6 text-center text-slate-500 gap-2">
                      <ColIcon class="w-8 h-8 text-slate-600" />
                      <p class="text-xs font-semibold text-slate-400">Nenhuma tarefa nesta coluna</p>
                      <p class="text-[11px] text-slate-600">Altere a aba ou ajuste os filtros acima</p>
                    </div>
                  );
                }

                return colTasks.map(t => (
                  <div
                    key={t.id}
                    onClick={() => selectedKanbanTaskSignal.value = t}
                    class="p-4 bg-slate-900 border border-slate-800 active:border-cyan-500/50 rounded-2xl shadow-md transition space-y-3 cursor-pointer group"
                  >
                    {/* Stakeholder Tag & Priority */}
                    <div class="flex items-center justify-between text-xs text-slate-400">
                      <span class="font-bold text-slate-200 truncate flex items-center gap-1.5">
                        <User class="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                        <span class="truncate">{t.stakeholder_name || t.contact_phone}</span>
                      </span>
                      {t.priority === 'high' && (
                        <span class="px-2 py-0.5 rounded text-[10px] font-extrabold bg-red-500/20 text-red-300 border border-red-500/30 shrink-0">
                          Alta
                        </span>
                      )}
                    </div>

                    {/* Task Description */}
                    <p class="text-xs font-semibold text-slate-100 group-hover:text-cyan-300 transition leading-snug">
                      {t.description}
                    </p>

                    {/* Metadata & Quick Status Action */}
                    <div class="flex flex-col gap-2 pt-2 border-t border-slate-800/60">
                      <div class="flex flex-wrap items-center justify-between text-[11px] text-slate-400 gap-2">
                        <div class="flex items-center gap-2">
                          {t.assignee && (
                            <span class="font-mono text-slate-300 flex items-center gap-1 bg-slate-950 px-2 py-0.5 rounded border border-slate-800">
                              <User class="w-3 h-3 text-slate-400 shrink-0" />
                              <span>{t.assignee}</span>
                            </span>
                          )}
                          {t.due_date && (
                            <span class="font-mono text-amber-300 flex items-center gap-1 bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20">
                              <Calendar class="w-3 h-3" />
                              <span>{new Date(t.due_date).toLocaleDateString('pt-BR')}</span>
                            </span>
                          )}
                        </div>

                        <div class="flex items-center gap-1.5 text-slate-500">
                          {t.notes && <FileText class="w-4 h-4 text-cyan-400" title="Possui notas ricas" />}
                          {t.links && <LinkIcon class="w-4 h-4 text-amber-400" title="Possui links anexados" />}
                        </div>
                      </div>

                      {/* Mobile Quick Status Switcher */}
                      <div class="flex items-center gap-1.5 pt-1.5 border-t border-slate-800/40 text-[11px]">
                        <span class="text-[10px] font-bold text-slate-500 uppercase tracking-wider shrink-0">Mover:</span>
                        <div class="flex items-center gap-1 overflow-x-auto flex-1">
                          {columns.map(c => {
                            if (c.id === t.status) return null;
                            const QuickIcon = c.icon;
                            return (
                              <button
                                key={c.id}
                                type="button"
                                onClick={(e) => handleStatusQuickChange(e, t.id, c.id)}
                                class={`px-2 py-1 rounded-lg border text-[10px] font-bold flex items-center gap-1 transition cursor-pointer min-h-[32px] ${c.badgeBg}`}
                                title={`Mover para ${c.title}`}
                              >
                                <QuickIcon class="w-3 h-3 shrink-0" />
                                <span>{c.shortTitle}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                  </div>
                ));
              })()}
            </div>

            {/* Desktop View: Full Multi-Column Board (SM+) */}
            <div class="hidden sm:flex flex-1 p-4 overflow-x-auto overflow-y-hidden">
              <div class="grid grid-cols-1 md:grid-cols-4 gap-4 h-full min-w-[1000px] w-full">
                {columns.map(col => {
                  const colTasks = tasks.filter(t => (t.status || 'pending') === col.id);
                  const ColIcon = col.icon;

                  return (
                    <div
                      key={col.id}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, col.id)}
                      class={`flex flex-col h-full bg-slate-900/50 border ${col.color} rounded-2xl p-3 shadow-lg overflow-hidden transition`}
                    >
                      {/* Column Header */}
                      <div class="flex items-center justify-between mb-3 pb-2 border-b border-slate-800 shrink-0">
                        <h3 class="text-xs font-extrabold text-slate-200 flex items-center gap-1.5">
                          <ColIcon class="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                          <span>{col.title}</span>
                        </h3>
                        <span class={`px-2 py-0.5 rounded-full text-[11px] font-mono font-bold ${col.badgeBg}`}>
                          {colTasks.length}
                        </span>
                      </div>

                      {/* Column Task Cards Container */}
                      <div class="flex-1 overflow-y-auto space-y-3 pr-1">
                        {colTasks.length === 0 ? (
                          <div class="h-32 border border-dashed border-slate-800/80 rounded-xl flex items-center justify-center text-[11px] text-slate-600 font-medium">
                            Arraste cartões para cá
                          </div>
                        ) : (
                          colTasks.map(t => (
                            <div
                              key={t.id}
                              draggable
                              onDragStart={(e) => handleDragStart(e, t.id)}
                              onClick={() => selectedKanbanTaskSignal.value = t}
                              class="p-3.5 bg-slate-900 hover:bg-slate-850 border border-slate-800 hover:border-cyan-500/50 rounded-xl shadow-md transition transform hover:-translate-y-0.5 cursor-pointer space-y-2.5 group"
                            >
                              {/* Stakeholder Tag */}
                              <div class="flex items-center justify-between text-[11px] text-slate-400">
                                <span class="font-bold text-slate-300 truncate flex items-center gap-1">
                                  <User class="w-3 h-3 text-cyan-400 shrink-0" />
                                  <span class="truncate">{t.stakeholder_name || t.contact_phone}</span>
                                </span>
                                {t.priority === 'high' && (
                                  <span class="px-1.5 py-0.5 rounded text-[9px] font-extrabold bg-red-500/20 text-red-300 border border-red-500/30 shrink-0">
                                    Alta
                                  </span>
                                )}
                              </div>

                              {/* Task Description */}
                              <p class="text-xs font-semibold text-slate-100 group-hover:text-cyan-300 transition leading-snug line-clamp-3">
                                {t.description}
                              </p>

                              {/* Badges & Indicators */}
                              <div class="flex flex-wrap items-center justify-between text-[10px] text-slate-400 pt-1 border-t border-slate-800/60 gap-2">
                                <div class="flex items-center gap-2">
                                  {t.assignee && (
                                    <span class="font-mono text-slate-300 flex items-center gap-1 bg-slate-950 px-1.5 py-0.5 rounded border border-slate-800">
                                      <User class="w-3 h-3 text-slate-400 shrink-0" />
                                      <span>{t.assignee}</span>
                                    </span>
                                  )}
                                  {t.due_date && (
                                    <span class="font-mono text-amber-300 flex items-center gap-1 bg-amber-500/10 px-1.5 py-0.5 rounded border border-amber-500/20">
                                      <Calendar class="w-3 h-3" />
                                      <span>{new Date(t.due_date).toLocaleDateString('pt-BR')}</span>
                                    </span>
                                  )}
                                </div>

                                <div class="flex items-center gap-1 text-slate-500">
                                  {t.notes && <FileText class="w-3.5 h-3.5 text-cyan-400" title="Possui notas ricas" />}
                                  {t.links && <LinkIcon class="w-3.5 h-3.5 text-amber-400" title="Possui links anexados" />}
                                </div>
                              </div>

                            </div>
                          ))
                        )}
                      </div>

                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

      </div>

      {/* Task Edit & Rich View Modal */}
      <TaskDetailModal />
    </div>
  );
}
